import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { useStreamingTracks } from '../hooks/useStreamingTracks';
import { streamingSectionPath, streamingTrackPath } from '../streamingPaths';
import { streamingBlockMessages as messages } from '../streamingBlockMessages';
import { StreamingBlock } from '../parts/StreamingBlock';
import { RankedRow } from '../parts/RankedRow';
import { TrackName } from '../parts/TrackName';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

const BLOCK_ROWS = 5;

interface TopTracksBlockProps {
  accountId: number;
  days: number;
  onNavigate: (path: string) => void;
}

/** The five tracks with the most streams in the period (design b1). */
const TopTracksBlockComponent: React.FC<TopTracksBlockProps> = ({ accountId, days, onNavigate }) => {
  const intl = useIntl();
  const { data, isLoading, error, retry } = useStreamingTracks(accountId, days);
  const tracks = useMemo(() => (data?.tracks ?? []).slice(0, BLOCK_ROWS), [data]);
  const top = useMemo(() => Math.max(0, ...tracks.map((track) => track.streams ?? 0)), [tracks]);

  return (
    <StreamingBlock
      title={intl.formatMessage(messages.topTracks)}
      moreLabel={intl.formatMessage(messages.allTracks)}
      onMore={() => onNavigate(streamingSectionPath('tracks', days))}
      isLoading={isLoading}
      error={error}
      retry={retry}
      testId="streaming-block-tracks"
    >
      {tracks.length === 0 ? (
        <p className={styles.faint}>{intl.formatMessage(messages.emptyList)}</p>
      ) : (
        <ol className={styles.rows}>
          {tracks.map((track, index) => (
            <RankedRow
              key={track.key}
              rank={index + 1}
              name={<TrackName name={track.name} imageUrl={track.imageUrl} />}
              share={top > 0 && track.streams !== null ? track.streams / top : null}
              value={track.streams}
              changePct={track.changePct}
              onOpen={() => onNavigate(streamingTrackPath(track.key, days))}
            />
          ))}
        </ol>
      )}
    </StreamingBlock>
  );
};

export const TopTracksBlock = TypedMemo(TopTracksBlockComponent);
